import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ItemCard, ItemCardSet } from '../../types/pointItemNote';
import { resolveItemCardImageSrc } from '../../utils/itemCardImage';

export type MemberItemGrantTarget = {
  id: string;
  label: string;
};

export type BulkStoreCartResult = {
  added: number;
  skipped: number;
};

type Props = {
  open: boolean;
  itemCards: ItemCardSet;
  targets: MemberItemGrantTarget[];
  onClose: () => void;
  /** 장바구니 담기 → 부모에서 학생 블럭 드래프트로 등록 (즉시 저장하지 않음) */
  onAddToCart: (card: ItemCard) => BulkStoreCartResult;
};

const CoinIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="10" fill="#f39c12" />
    <circle cx="12" cy="12" r="8" fill="#f1c40f" />
    <text
      x="12"
      y="16"
      fontSize="12"
      fontWeight="bold"
      fill="#e67e22"
      textAnchor="middle"
    >
      ₩
    </text>
  </svg>
);

const CardPlaceholder: React.FC<{ size?: number }> = ({ size = 48 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <rect
      x="2"
      y="6"
      width="12"
      height="16"
      rx="2"
      fill="#f0f4f3"
      stroke="#555"
      strokeWidth="1.5"
      transform="rotate(-10 8 14)"
    />
    <rect
      x="6"
      y="4"
      width="12"
      height="16"
      rx="2"
      fill="#f8f8f8"
      stroke="#555"
      strokeWidth="1.5"
    />
    <rect
      x="10"
      y="6"
      width="12"
      height="16"
      rx="2"
      fill="#fafafa"
      stroke="#555"
      strokeWidth="1.5"
      transform="rotate(10 16 14)"
    />
  </svg>
);

/** 일괄 아이템 구입 — 스토어 UI, 장바구니 담기 후 구매하기로 반영 */
const MemberItemGrantModal: React.FC<Props> = ({
  open,
  itemCards,
  targets,
  onClose,
  onAddToCart,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    kind: 'ok' | 'warn';
    message: string;
  } | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelectedId(null);
    setToast(null);
  }, [open]);

  const storeItems = useMemo(() => itemCards.storeItem ?? [], [itemCards.storeItem]);
  const selected = storeItems.find((c) => c.itemCardId === selectedId) ?? null;
  const selectedCost =
    selected && typeof selected.cost === 'number' ? selected.cost : 0;

  const renderThumb = (item: ItemCard, size = 48) => {
    const src = resolveItemCardImageSrc(item.cardImage);
    if (src) {
      return <img src={src} alt="" />;
    }
    return <CardPlaceholder size={size} />;
  };

  const handleAddToCart = () => {
    if (!selected) return;
    const result = onAddToCart(selected);
    if (result.added === 0) {
      setToast({ kind: 'warn', message: '코인이 부족해 담을 수 없습니다.' });
    } else if (result.skipped > 0) {
      setToast({
        kind: 'warn',
        message: `${result.added}명 담음 · ${result.skipped}명 코인 부족`,
      });
    } else {
      setToast({
        kind: 'ok',
        message: `${selected.name || '(이름 없음)'} · ${result.added}명 장바구니 담기`,
      });
    }
    setSelectedId(null);
    window.setTimeout(() => setToast(null), 1800);
  };

  if (!open) return null;

  return createPortal(
    <div className="cp-apn-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="cp-apn-modal cp-pin-store-modal"
        data-tutorial-pin="item-grant-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-pin-item-grant-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cp-apn-modal-header">
          <h2 id="cp-pin-item-grant-title">일괄 아이템 구입</h2>
          <button type="button" className="cp-apn-icon-btn" onClick={onClose} aria-label="닫기">
            ×
          </button>
        </header>

        <div className="cp-pin-store-tabs" role="tablist">
          <button type="button" className="is-active" role="tab" aria-selected>
            아이템 카드 구입
          </button>
          <button type="button" className="is-disabled" role="tab" aria-selected={false} disabled>
            프로필 카드 구입
          </button>
        </div>

        <div className="cp-pin-store-body">
          {storeItems.length === 0 ? (
            <div className="cp-pin-store-empty">
              <p>아이템 없음</p>
              <p className="cp-pin-store-empty-hint">
                아이템 카드 설정의 스토어 탭에서 아이템을 등록해주세요.
              </p>
            </div>
          ) : (
            <div className="cp-pin-store-grid">
              {storeItems.map((item) => {
                const cost = typeof item.cost === 'number' ? item.cost : 0;
                const selectedCard = selectedId === item.itemCardId;
                return (
                  <button
                    key={item.itemCardId}
                    type="button"
                    className={`cp-pin-store-card${selectedCard ? ' is-selected' : ''}`}
                    onClick={() => setSelectedId(selectedCard ? null : item.itemCardId)}
                  >
                    <div className="cp-pin-store-card-icon-wrap">
                      <div className="cp-pin-store-card-icon">{renderThumb(item)}</div>
                      {selectedCard ? (
                        <span className="cp-pin-store-card-check" aria-hidden>
                          ✓
                        </span>
                      ) : null}
                    </div>
                    <p className="cp-pin-store-card-name">{item.name || '(이름 없음)'}</p>
                    {item.memo?.trim() ? (
                      <p className="cp-pin-store-card-memo">{item.memo}</p>
                    ) : (
                      <p className="cp-pin-store-card-memo">&nbsp;</p>
                    )}
                    <div className="cp-pin-store-card-meta">
                      <span className="cp-pin-store-card-cost">
                        <CoinIcon />
                        <span>×{cost}</span>
                      </span>
                      {item.type === 'active' ? (
                        <span className="cp-pin-store-card-uses">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                            <path
                              d="M12 21.35L10.55 20.03C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5C22 12.27 18.6 15.36 13.45 20.03L12 21.35Z"
                              fill="#ff4757"
                            />
                          </svg>
                          <span>×{item.useCount || 1}</span>
                        </span>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <footer className="cp-pin-store-footer">
          <div className="cp-pin-store-coins cp-pin-bulk-store-meta">
            <span>
              선택된 멤버: <strong>{targets.length}</strong>명
            </span>
            {selected ? (
              <span className="cp-pin-bulk-store-cost">
                구입 비용:
                <CoinIcon size={16} />
                <strong>×{selectedCost}</strong>
                <span className="cp-pin-bulk-store-cost-hint">/인</span>
              </span>
            ) : null}
          </div>
          <button
            type="button"
            className="cp-pin-store-buy-btn"
            data-tutorial-pin="item-grant-confirm"
            disabled={!selected || targets.length === 0}
            onClick={handleAddToCart}
          >
            장바구니 담기
          </button>
        </footer>

        {toast ? (
          <div
            className={`cp-pin-store-toast ${toast.kind === 'ok' ? 'is-ok' : 'is-warn'}`}
            role="status"
          >
            {toast.message}
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  );
};

export default MemberItemGrantModal;
